import { useEffect, useState, useCallback } from 'react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { Loader2, RefreshCw, AlertTriangle, BellRing, Sparkles, Megaphone } from 'lucide-react';

const SITE_FLAGS: Record<string, string> = {
  MLA: '\u{1F1E6}\u{1F1F7}',
  MLM: '\u{1F1F2}\u{1F1FD}',
  MLC: '\u{1F1E8}\u{1F1F1}',
  MCO: '\u{1F1E8}\u{1F1F4}',
  MLU: '\u{1F1FA}\u{1F1FE}',
  MLB: '\u{1F1E7}\u{1F1F7}',
};

interface HealthData {
  seller: any;
  snapshot: any;
  notices: Array<any>;
}

function timeAgo(d: string | null | undefined): string {
  if (!d) return 'nunca';
  const ms = Date.now() - new Date(d).getTime();
  const h = Math.floor(ms / 3_600_000);
  if (h < 1) return 'hace minutos';
  if (h < 24) return `hace ${h}h`;
  return `hace ${Math.floor(h / 24)}d`;
}

export function SellerHealthCards() {
  const { sellerId } = useUIStore();
  const [data, setData] = useState<HealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await api.get<HealthData>(`/seller/health?seller_id=${sellerId}`);
    if (res.success && res.data) setData(res.data);
    setLoading(false);
  }, [sellerId]);

  useEffect(() => { load(); }, [load]);

  const handleRefresh = async () => {
    setRefreshing(true);
    await api.post('/seller/health/refresh', { seller_id: sellerId });
    await load();
    setRefreshing(false);
  };

  const markRead = async (id: number) => {
    await api.patch(`/seller/health/notices/${id}/read`, { seller_id: sellerId });
    setData(prev => prev ? { ...prev, notices: prev.notices.map(n => n.id === id ? { ...n, is_read: true } : n) } : prev);
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="animate-spin" /></div>;
  if (!data) return <div className="text-sm text-[var(--color-text-tertiary)]">Sin datos</div>;

  const { seller, snapshot, notices } = data;
  const errors: Record<string, string> = snapshot?.errors || {};
  const lastSync = seller?.health_last_synced_at;
  const stale = lastSync && (Date.now() - new Date(lastSync).getTime()) > 48 * 3_600_000;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between text-sm">
        <span className="text-[var(--color-text-secondary)]">
          Última sincronización: <strong>{timeAgo(lastSync)}</strong>
          {stale && <span className="ml-2 text-red-500"><AlertTriangle size={14} className="inline mr-1" />desactualizado</span>}
        </span>
        <button onClick={handleRefresh} disabled={refreshing}
          className="flex items-center gap-1 px-3 py-1.5 bg-[var(--color-primary)] text-white rounded text-xs disabled:opacity-50">
          <RefreshCw size={12} className={refreshing ? 'animate-spin' : ''} /> Refrescar ahora
        </button>
      </div>

      <CardA seller={seller} snapshot={snapshot} error={errors.profile || errors.listing_types} />
      <CardB seller={seller} reputation={snapshot?.reputation} error={errors.reputation} />
      <CardC notices={notices} onMarkRead={markRead} error={errors.notices} />
    </div>
  );
}

function CardA({ seller, snapshot, error }: any) {
  const sites: string[] = seller?.active_sites || [];
  const childUsers = seller?.marketplace_user_ids || {};
  const restrictions = snapshot?.restrictions || [];
  const brands = snapshot?.brands || [];

  return (
    <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg p-4">
      <h3 className="text-sm font-semibold mb-3">Identidad & Configuración</h3>
      {error && <div className="text-xs text-amber-600 mb-2">⚠ {error}</div>}
      <div className="space-y-2 text-sm">
        <div><strong>{seller?.nickname}</strong> · seller_ml_id: {seller?.seller_ml_id} · {seller?.country_id}</div>
        {seller?.ml_user_email && <div className="text-xs text-[var(--color-text-secondary)]">✉ {seller.ml_user_email}</div>}
        <div className="text-xs">
          Status: <span className={seller?.ml_status === 'active' ? 'text-green-500' : 'text-red-500'}>● {seller?.ml_status || '—'}</span>
          {' · '}Points: {seller?.seller_points ?? '—'}
        </div>
        <div>
          <div className="text-xs text-[var(--color-text-tertiary)] mb-1">Sites activos:</div>
          <div className="flex flex-wrap gap-2">
            {sites.map((s: string) => (
              <span key={s} className="text-xs bg-[var(--color-bg)] border border-[var(--color-border)] rounded px-2 py-0.5">
                {SITE_FLAGS[s] || ''} {s}
              </span>
            ))}
          </div>
        </div>
        <div>
          <div className="text-xs text-[var(--color-text-tertiary)] mb-1">Child users:</div>
          <div className="text-xs font-mono space-y-0.5">
            {Object.entries(childUsers).map(([site, val]: [string, any]) => {
              const userId = typeof val === 'object' ? val.user_id : val;
              const lt = typeof val === 'object' ? val.logistic_type : '';
              return <div key={site}>{site} → {userId}{lt ? ` (${lt})` : ''}</div>;
            })}
          </div>
        </div>
        <div className="text-xs">Restricciones: {restrictions.length === 0 ? 'Ninguna' : restrictions.length}</div>
        <div className="text-xs">Marcas registradas: {brands.length === 0 ? 'Ninguna' : brands.map((b: any) => b.name || b).join(', ')}</div>
      </div>
    </div>
  );
}

function CardB({ seller, reputation, error }: any) {
  if (error) {
    return (
      <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg p-4">
        <h3 className="text-sm font-semibold mb-3">Salud / Reputación</h3>
        <div className="text-xs text-amber-600">⚠ {error}</div>
      </div>
    );
  }
  const m = reputation?.metrics || {};
  return (
    <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg p-4">
      <h3 className="text-sm font-semibold mb-3">Salud / Reputación</h3>
      <div className="space-y-2 text-sm">
        <div>Nivel: <strong>{seller?.mercadolibre_level || reputation?.level_id || '—'}</strong></div>
        {reputation?.transactions && (
          <div className="text-xs">Ventas (60d): <strong>{reputation.transactions.completed ?? '—'}</strong></div>
        )}
        <div className="text-xs">Tasa de claims: <strong>{m.claims?.rate != null ? `${(m.claims.rate * 100).toFixed(2)}%` : '—'}</strong></div>
        <div className="text-xs">Cancelaciones: <strong>{m.cancellations?.rate != null ? `${(m.cancellations.rate * 100).toFixed(2)}%` : '—'}</strong></div>
        <div className="text-xs">Demora despacho: <strong>{m.delayed_handling_time?.rate != null ? `${(m.delayed_handling_time.rate * 100).toFixed(2)}%` : '—'}</strong></div>
      </div>
    </div>
  );
}

function CardC({ notices, onMarkRead, error }: any) {
  if (error) {
    return (
      <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg p-4">
        <h3 className="text-sm font-semibold mb-3">Novedades ML</h3>
        <div className="text-xs text-amber-600">⚠ {error}</div>
      </div>
    );
  }
  const list: any[] = Array.isArray(notices) ? notices : [];
  const unread = list.filter((n: any) => !n.is_read);
  const icon = (cat: string) =>
    cat === 'ALERT' ? <BellRing size={14} className="text-red-500" /> :
    cat === 'NEW' ? <Sparkles size={14} className="text-blue-500" /> :
    <Megaphone size={14} className="text-gray-500" />;
  return (
    <div className="bg-[var(--color-bg-secondary)] border border-[var(--color-border)] rounded-lg p-4">
      <h3 className="text-sm font-semibold mb-3">
        Novedades ML
        {unread.length > 0 && <span className="text-xs text-amber-500 ml-2">({unread.length} sin leer)</span>}
      </h3>
      {list.length === 0 && <div className="text-xs text-[var(--color-text-tertiary)]">No hay novedades</div>}
      <div className="space-y-2 max-h-80 overflow-auto">
        {list.slice(0, 20).map((n: any) => (
          <div key={n.id} className={`text-xs flex items-start gap-2 p-2 rounded ${n.is_read ? 'opacity-60' : 'bg-[var(--color-bg)]'}`}>
            {icon(n.category)}
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{n.title}</div>
              <div className="text-[var(--color-text-tertiary)]">
                {n.published_at ? new Date(n.published_at).toISOString().slice(0, 10) : ''}
              </div>
              {n.body && <div className="text-[var(--color-text-secondary)] mt-1 line-clamp-2">{n.body}</div>}
            </div>
            {!n.is_read && (
              <button onClick={() => onMarkRead(n.id)} className="text-[var(--color-primary)] hover:underline shrink-0">
                Leído
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
